import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import examApi from "../../../redux/exam/examApi";
import { studyDashboardQueryKeys } from "./studyDashboardQueryKeys";
import {
  DASHBOARD_CACHE_TIME,
  DASHBOARD_SLOW_ANALYTICS_CACHE_TIME,
  DASHBOARD_SLOW_ANALYTICS_STALE_TIME,
  DASHBOARD_STALE_TIME,
  clearDashboardHttpMisses,
  retryDashboardQuery,
  runDashboardQuery,
} from "./studyDashboardCacheSync";
import {
  fetchStudyDashboardDailyGoal,
  fetchDashboardExploreStore,
  fetchDashboardHandbookList,
  fetchDashboardStoreBanner,
  fetchDashboardHomeSliderBanners,
  fetchDashboardUserProfile,
  fetchDashboardSubjectList,
  fetchStudyDashboardLeaderboard,
  fetchStudyDashboardProgress,
  fetchStudyDashboardResumeVideo,
  fetchStudyDashboardResumeTests,
  fetchStudyDashboardScheduleContent,
  fetchStudyDashboardScheduleWeek,
  fetchStudyDashboardStreak,
  fetchStudyDashboardTopWeakTopics,
  fetchStudyDashboardTestWiseAnalysis,
  fetchStudyDashboardSubjectPerformance,
  fetchStoreDeltaUpgrade,
} from "./studyDashboardApi";

const dashboardQueryOptions = {
  staleTime: DASHBOARD_STALE_TIME,
  cacheTime: DASHBOARD_CACHE_TIME,
  refetchOnMount: true,
  refetchOnWindowFocus: false,
  retry: retryDashboardQuery,
};

const useDashboardQuery = ({ queryKey, queryFn, ...options }) =>
  useQuery({
    queryKey,
    queryFn: () => runDashboardQuery(queryKey, queryFn),
    ...dashboardQueryOptions,
    ...options,
  });

export const useStudyDashboardAuth = () => {
  const token = useSelector((state) => state.auth.authToken);
  const tokenType = useSelector((state) => state.auth.tokenType);
  const courseId = useSelector(
    (state) => state.userPreference?.courseDetails?.[0]?.course_id
  );
  const batchId = useSelector((state) => state.userPreference?.batchDetails?.batch_id);
  const classId = useSelector((state) => state.userPreference?.classDetails?.class_id);
  const targetId = useSelector(
    (state) => state.userPreference?.targetDetails?.[0]?.target_id
  );

  const authorization =
    token && tokenType ? `${String(tokenType).trim()} ${String(token).trim()}` : "";

  return {
    authorization,
    courseId,
    batchId,
    classId,
    targetId,
    isAuthenticated: Boolean(authorization),
    hasSchedulePrefs: Boolean(authorization && courseId && batchId),
  };
};

export const useStudyDashboardProgressQuery = () => {
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return useDashboardQuery({
    queryKey: studyDashboardQueryKeys.progress(authorization),
    queryFn: () => fetchStudyDashboardProgress(authorization),
    enabled: isAuthenticated,
  });
};

export const useStudyDashboardStreakQuery = () => {
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return useDashboardQuery({
    queryKey: studyDashboardQueryKeys.streak(authorization),
    queryFn: () => fetchStudyDashboardStreak(authorization),
    enabled: isAuthenticated,
  });
};

export const useStudyDashboardLeaderboardQuery = () => {
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return useDashboardQuery({
    queryKey: studyDashboardQueryKeys.leaderboard(authorization),
    queryFn: () => fetchStudyDashboardLeaderboard(authorization),
    enabled: isAuthenticated,
  });
};

export const useStudyDashboardResumeVideoQuery = () => {
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return useDashboardQuery({
    queryKey: studyDashboardQueryKeys.resumeVideo(authorization),
    queryFn: () => fetchStudyDashboardResumeVideo(authorization),
    enabled: isAuthenticated,
  });
};

export const useStudyDashboardResumeTestsQuery = () => {
  const { authorization, isAuthenticated, courseId, batchId, classId, targetId } =
    useStudyDashboardAuth();

  return useDashboardQuery({
    queryKey: studyDashboardQueryKeys.resumeTests(
      authorization,
      courseId,
      batchId,
      classId,
      targetId
    ),
    queryFn: () =>
      fetchStudyDashboardResumeTests(authorization, {
        courseId,
        batchId,
        classId,
        targetId,
      }),
    enabled: isAuthenticated,
  });
};

export const useDashboardExploreStoreQuery = () => {
  const { authorization, isAuthenticated, courseId, classId, targetId } =
    useStudyDashboardAuth();

  return useDashboardQuery({
    queryKey: studyDashboardQueryKeys.exploreStore(authorization, courseId, classId, targetId),
    queryFn: () => fetchDashboardExploreStore(authorization),
    enabled: isAuthenticated,
  });
};

export const useDashboardHandbookListQuery = (productId = "", enabled = true) => {
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return useDashboardQuery({
    queryKey: studyDashboardQueryKeys.handbookList(authorization, productId),
    queryFn: () => fetchDashboardHandbookList(authorization, productId),
    enabled: isAuthenticated && enabled,
  });
};

export const useDashboardStoreBannerQuery = () => {
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return useDashboardQuery({
    queryKey: studyDashboardQueryKeys.storeBanner(authorization),
    queryFn: () => fetchDashboardStoreBanner(authorization),
    enabled: isAuthenticated,
  });
};

export const useDashboardHomeSliderBannersQuery = () => {
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return useDashboardQuery({
    queryKey: studyDashboardQueryKeys.homeSliderBanners(authorization),
    queryFn: () => fetchDashboardHomeSliderBanners(authorization),
    enabled: isAuthenticated,
  });
};

export const useDashboardUserProfileQuery = () => {
  const { authorization, isAuthenticated, targetId } = useStudyDashboardAuth();

  return useDashboardQuery({
    queryKey: studyDashboardQueryKeys.userProfile(authorization, targetId),
    queryFn: () => fetchDashboardUserProfile(authorization),
    enabled: isAuthenticated,
  });
};

export const useDashboardSubjectListQuery = () => {
  const { authorization, isAuthenticated, courseId, classId, targetId } =
    useStudyDashboardAuth();
  const profileQuery = useDashboardUserProfileQuery();
  const isCategorized = Number(profileQuery.data?.is_categorized_sub) === 1;

  return useDashboardQuery({
    queryKey: studyDashboardQueryKeys.subjectList(
      authorization,
      courseId,
      classId,
      targetId
    ),
    queryFn: () => fetchDashboardSubjectList(authorization, courseId, classId),
    enabled:
      isAuthenticated &&
      courseId != null &&
      classId != null &&
      Boolean(profileQuery.data) &&
      !isCategorized,
  });
};

export const useStudyDashboardPracticeListQuery = () => {
  const { authorization, isAuthenticated, targetId } = useStudyDashboardAuth();

  return useDashboardQuery({
    queryKey: studyDashboardQueryKeys.practiceList(authorization, targetId),
    queryFn: async () => {
      const response = await examApi.getPracticeList();
      return response?.data?.data ?? null;
    },
    enabled: isAuthenticated,
  });
};

export const useStudyDashboardDailyGoalQuery = (enabled) => {
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return useDashboardQuery({
    queryKey: studyDashboardQueryKeys.dailyGoal(authorization),
    queryFn: () => fetchStudyDashboardDailyGoal(authorization),
    enabled: isAuthenticated && enabled,
  });
};

export const useStudyDashboardTopWeakTopicsQuery = () => {
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return useDashboardQuery({
    queryKey: studyDashboardQueryKeys.topWeakTopics(authorization),
    queryFn: () => fetchStudyDashboardTopWeakTopics(authorization),
    enabled: isAuthenticated,
    staleTime: DASHBOARD_SLOW_ANALYTICS_STALE_TIME,
    cacheTime: DASHBOARD_SLOW_ANALYTICS_CACHE_TIME,
  });
};

export const useStudyDashboardTestWiseAnalysisQuery = () => {
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return useDashboardQuery({
    queryKey: studyDashboardQueryKeys.testWiseAnalysis(authorization),
    queryFn: () => fetchStudyDashboardTestWiseAnalysis(authorization),
    enabled: isAuthenticated,
  });
};

export const useStudyDashboardSubjectPerformanceQuery = () => {
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return useDashboardQuery({
    queryKey: studyDashboardQueryKeys.subjectPerformance(authorization),
    queryFn: () => fetchStudyDashboardSubjectPerformance(authorization),
    enabled: isAuthenticated,
    staleTime: DASHBOARD_SLOW_ANALYTICS_STALE_TIME,
    cacheTime: DASHBOARD_SLOW_ANALYTICS_CACHE_TIME,
  });
};

export const useStudyDashboardScheduleWeekQuery = () => {
  const { authorization, courseId, batchId, hasSchedulePrefs } =
    useStudyDashboardAuth();

  return useDashboardQuery({
    queryKey: studyDashboardQueryKeys.scheduleWeek(authorization, courseId, batchId),
    queryFn: () =>
      fetchStudyDashboardScheduleWeek(authorization, courseId, batchId),
    enabled: hasSchedulePrefs,
  });
};

export const useStudyDashboardScheduleContentQuery = ({
  dayName,
  rawDate,
  enabled = true,
}) => {
  const { authorization, courseId, batchId, hasSchedulePrefs } =
    useStudyDashboardAuth();

  const canFetch = Boolean(enabled && hasSchedulePrefs && dayName && rawDate);

  return useDashboardQuery({
    queryKey: studyDashboardQueryKeys.scheduleContent(
      authorization,
      courseId,
      batchId,
      dayName,
      rawDate
    ),
    queryFn: () =>
      fetchStudyDashboardScheduleContent(
        authorization,
        courseId,
        batchId,
        dayName,
        rawDate
      ),
    enabled: canFetch,
  });
};

export const useInvalidateStudyDashboardProgress = () => {
  const queryClient = useQueryClient();
  const { authorization } = useStudyDashboardAuth();

  return () =>
    queryClient.invalidateQueries({
      queryKey: studyDashboardQueryKeys.progress(authorization),
    });
};

export const useRefreshStudyDashboardAfterGoalSave = () => {
  const queryClient = useQueryClient();
  const { authorization } = useStudyDashboardAuth();

  return async (savedGoal) => {
    if (savedGoal != null && authorization) {
      queryClient.setQueryData(
        studyDashboardQueryKeys.dailyGoal(authorization),
        (current) => (current ? { ...current, saved_goal: savedGoal } : current)
      );
    }

    const dailyGoalKey = studyDashboardQueryKeys.dailyGoal(authorization);
    clearDashboardHttpMisses(dailyGoalKey);
    clearDashboardHttpMisses(studyDashboardQueryKeys.progress(authorization));
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: studyDashboardQueryKeys.progress(authorization),
      }),
      queryClient.fetchQuery({
        queryKey: dailyGoalKey,
        queryFn: () =>
          runDashboardQuery(dailyGoalKey, () =>
            fetchStudyDashboardDailyGoal(authorization)
          ),
        staleTime: 0,
      }),
    ]);
  };
};

export const useFetchStudyDashboardDailyGoal = () => {
  const queryClient = useQueryClient();
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return () => {
    if (!isAuthenticated) return Promise.resolve(null);
    const queryKey = studyDashboardQueryKeys.dailyGoal(authorization);
    clearDashboardHttpMisses(queryKey);
    return queryClient.fetchQuery({
      queryKey,
      queryFn: () =>
        runDashboardQuery(queryKey, () => fetchStudyDashboardDailyGoal(authorization)),
      staleTime: 0,
    });
  };
};

export const useStoreDeltaUpgradeQuery = (packageId) => {
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return useQuery({
    queryKey: ["storeDeltaUpgrade", packageId, authorization],
    queryFn: () => fetchStoreDeltaUpgrade(authorization, packageId),
    enabled: Boolean(isAuthenticated && packageId),
    ...dashboardQueryOptions,
  });
};
